import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  W3wIcon,
  LatLongIcon,
  CopyIcon,
} from '@rsos/assets/icons';
import { Box, Text, Loader } from '@rsos/base-components';
import { Button } from '@rsos/components/capstone/base';
import { CloseButton } from '@rsos/components/capstone/base';
import {
  selectActiveServices,
  selectBasemapService,
} from '@rsos/reduxDataStore';
import { fetchWhat3words } from '@rsos/reduxDataStore/src/features/what3words/what3wordsAPI';
import { createWebMercatorViewport } from '../utils';
import {
  PopoverContainer,
  HeaderSection,
  ContentSection,
  ContentSectionItem,
  AttributeRow,
  HeaderAttributeRow,
  TitleTruncate,
} from './ClickToIdentifyPopover.styles';
import LocalCopyButton from './LocalCopyButton';
import { hideMapClickInfo } from './clickToIdentifySlice';
import { formatValue, formatFieldName, formatCoordinate } from './formatters';
import type {
  Position,
  ViewState,
  What3WordsResponse,
  MapClickInfo,
  RootState,
  ClickToIdentifyPopoverProps,
} from './types';

/**
 * Another one-off change for Vermont.
 * Geocomm claims its NOT in their data dictionary, but it is.
 * Due to tight timeline, we'll ignore it for now.
 * TODO: Remove this once Geocomm fixes their data dictionary.
 */
const VERMONT_IGNORE_SOURCE_TITLES = ['esbpsap'];
const VERMONT_IGNORE_GIS_PARTIAL_URL = 'vermont.geocomm.cloud';

const MIN_CONTENT_HEADER_SECTION_HEIGHT = 60;
const MIN_CONTENT_SECTION_ITEM_HEIGHT = 250;
const MAX_CONTENT_SECTION_ITEM_HEIGHT = 250;

const POPOVER_WIDTH = 320; // keep in sync with styles
const POPOVER_OFFSET = 10; // margin offset from anchor

const ClickToIdentifyPopover: React.FC<ClickToIdentifyPopoverProps> = ({
  overlay,
}) => {
  const dispatch = useDispatch();
  const [position, setPosition] = useState<Position | null>(null);
  const [what3Words, setWhat3Words] = useState<string | null>(null);
  const [attributeIdx, setAttributeIdx] = useState<number>(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  const mapClickInfo = useSelector(
    (state: RootState) => state.clickToIdentify?.mapClickInfo,
  );

  const activeGISServices = useSelector(selectActiveServices);

  const basemapService = useSelector(selectBasemapService);

  const basemap = useSelector((state: RootState) => state.sinatra?.ui?.basemap);

  const reposition = useCallback(
    (viewState: ViewState, clickInfo: MapClickInfo | null) => {
      if (!clickInfo?.longitude || !clickInfo?.latitude) return;

      const viewport = createWebMercatorViewport(viewState, basemap);

      try {
        const [rawX, rawY] = viewport.project([
          clickInfo.longitude,
          clickInfo.latitude,
        ]);

        const viewportHeight = viewState.height;
        const viewportWidth = viewState.width;

        // default desired placement: above the point
        let desiredPlacement: 'top' | 'bottom' = 'top';
        const popoverHeight = popoverRef.current?.offsetHeight || 0;

        // Choose placement based on available space
        const spaceAbove = rawY;
        const spaceBelow = viewportHeight - rawY;
        if (
          popoverHeight + POPOVER_OFFSET > spaceAbove &&
          spaceBelow > spaceAbove
        ) {
          desiredPlacement = 'bottom';
        }

        // Compute anchored y with offset depending on placement
        const anchoredY =
          desiredPlacement === 'bottom'
            ? rawY + POPOVER_OFFSET
            : rawY - POPOVER_OFFSET;

        // Clamp x within viewport to keep the popover within horizontal bounds
        const clampedX = Math.min(
          Math.max(rawX, POPOVER_WIDTH / 2 + 4),
          viewportWidth - POPOVER_WIDTH / 2 - 4,
        );

        // Clamp y within viewport as a fallback
        const clampedY = Math.min(Math.max(anchoredY, 0), viewportHeight);

        setPlacement(desiredPlacement);
        setPosition({ x: clampedX, y: clampedY });
      } catch {
        /* suppress, fail gracefully */
      }
    },
    [basemap],
  );

  /**
   * View-state driven repositioning.
   * Triggered whenever the map overlay emits a view state change
   * (pan, zoom, pitch, bearing, or resize). Keeps the popover anchored
   * to the last clicked coordinate as the camera moves.
   */
  useEffect(() => {
    if (overlay) {
      overlay.onViewStateChange((viewState: ViewState) => {
        reposition(viewState, mapClickInfo || null);
      });
    }
  }, [overlay, mapClickInfo, reposition]);

  /**
   * Initial placement and teardown handling.
   * Triggered when a new `mapClickInfo` arrives or the overlay becomes available.
   * Positions the popover immediately on selection and clears it when the
   * selection is removed.
   */
  useEffect(() => {
    if (overlay && mapClickInfo) {
      const viewState = overlay.getViewState();
      reposition(viewState, mapClickInfo);
    }
    if (!mapClickInfo) {
      setPosition(null);
    }
  }, [overlay, mapClickInfo, reposition]);

  const handleClose = useCallback(() => {
    dispatch(hideMapClickInfo());
  }, [dispatch]);

  useEffect(() => {
    const layerId = mapClickInfo?.featureData?.parentFeatureLayerID;
    if (!layerId) {
      return;
    }

    const clickedId =
      typeof layerId === 'string'
        ? Number(layerId.split('-').pop())
        : Number(layerId);

    if (isNaN(clickedId)) {
      // eslint-disable-next-line no-console
      console.warn('Invalid layer ID format:', layerId);
      return;
    }

    const isActive = activeGISServices.some(service => {
      return service.id === clickedId;
    });

    const isBasemapActive =
      basemapService?.id && basemapService.id === clickedId;

    if (!isActive && !isBasemapActive) {
      handleClose();
    }
  }, [activeGISServices, mapClickInfo, handleClose, basemapService]);

  useEffect(() => {
    if (!mapClickInfo || !mapClickInfo.latitude || !mapClickInfo.longitude) {
      setWhat3Words(null);
      return;
    }

    const fetchWhat3WordsData = async () => {
      try {
        const response = (await fetchWhat3words(
          mapClickInfo.latitude,
          mapClickInfo.longitude,
        )) as unknown as What3WordsResponse;
        setWhat3Words(response.words);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching what3words:', error);
        setWhat3Words(null);
      }
    };

    fetchWhat3WordsData();
  }, [mapClickInfo, mapClickInfo?.latitude, mapClickInfo?.longitude]);

  // Reset attribute index only when the click-to-identify info object changes
  useEffect(() => {
    if (mapClickInfo) {
      setAttributeIdx(0);
    }
  }, [mapClickInfo]);

  if (!mapClickInfo || !position) return null;

  const { featureData } = mapClickInfo;

  interface AttributeItem {
    label: string;
    node: React.ReactNode;
  }
  const renderAttributes = (): AttributeItem[] | null => {
    if (
      featureData?.attributes &&
      Object.keys(featureData.attributes).length > 0
    ) {
      const items = Object.entries(
        featureData.attributes as Record<string, Record<string, unknown>[]>,
      )
        .map(([sourceTitle, attributesArray]) => {
          try {
            if (
              basemapService?.gis_service_url.includes(
                VERMONT_IGNORE_GIS_PARTIAL_URL,
              ) &&
              VERMONT_IGNORE_SOURCE_TITLES.includes(sourceTitle)
            )
              return null;
          } catch (_) {
            // fail gracefully
          }
          if (!attributesArray || attributesArray.length === 0) return null;
          const allFields = new Set<string>();
          attributesArray.forEach(attr => {
            Object.keys(attr).forEach(key => {
              if (!key.startsWith('_') && !['OBJECTID', 'FID'].includes(key)) {
                allFields.add(key);
              }
            });
          });

          const fields = Array.from(allFields);

          return {
            label: sourceTitle,
            node: (
              <Box mt="12px" mb="4px" position="relative">
                <Box>
                  {attributesArray.map((attrs, index) => (
                    <Box key={index} mb="8px">
                      {fields.map(fieldName => {
                        const value = attrs[fieldName];
                        if (value === undefined) return null;

                        return (
                          <AttributeRow
                            key={`${sourceTitle}-${index}-${fieldName}`}
                          >
                            <Text size="small" color="secondaryText">
                              {formatFieldName(fieldName)}
                            </Text>

                            <Text
                              size="small"
                              color="primaryText"
                              textAlign={'right'}
                            >
                              {formatValue(value, fieldName)}
                            </Text>
                          </AttributeRow>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            ),
          } as AttributeItem;
        })
        .filter(Boolean) as AttributeItem[];

      return items.length ? items : null;
    }

    if (!featureData?.attributes || !featureData?.fields?.length) return null;

    const fields = featureData.fields.filter(
      field =>
        field?.name && // ensure field has a name
        !field.name.startsWith('__') && // skip internal fields
        !['OBJECTID', 'FID'].includes(field.name), // skip common system fields
    );

    const rows = fields
      .map(field => {
        if (!field?.name) return null;
        const value = featureData.attributes[field.name];
        if (value === undefined) return null;

        return (
          <AttributeRow key={field.name}>
            <Text size="small" color="secondaryText">
              {formatFieldName(field.alias || field.name)}
            </Text>

            <Text size="small" color="primaryText" textAlign={'right'}>
              {formatValue(value, field.name)}
            </Text>
          </AttributeRow>
        );
      })
      .filter(Boolean);

    return [
      {
        label: 'Feature Information',
        node: <>{rows}</>,
      } as AttributeItem,
    ];
  };

  const hasFeatureData =
    featureData?.attributes &&
    featureData?.fields &&
    featureData.fields.length > 0;
  const hasAttributesBySource =
    featureData?.attributes && Object.keys(featureData.attributes).length > 0;
  const attributes = (
    hasFeatureData || hasAttributesBySource ? renderAttributes() : []
  ) as AttributeItem[];

  const handlePrevAttribute = () => {
    if (attributes) {
      if (attributeIdx <= 0) {
        setAttributeIdx(attributes.length - 1);
      } else {
        setAttributeIdx(prev => Math.max(0, prev - 1));
      }
    }
  };

  const handleNextAttribute = () => {
    if (attributes) {
      if (attributeIdx >= attributes.length - 1) {
        setAttributeIdx(0);
      } else {
        setAttributeIdx(prev => Math.min(attributes.length - 1, prev + 1));
      }
    }
  };

  const coordinatesContent = (
    <Box mt="12px">
      <HeaderAttributeRow>
        <Text size="small" color="secondaryText">
          <LatLongIcon
            width={16}
            height={16}
            viewboxHeight={16}
            viewboxWidth={16}
          />
        </Text>
        <Box horizontal align="center">
          <Text size="small" color="primaryText" textAlign="center">
            {formatCoordinate(mapClickInfo.latitude)}°,{' '}
            {formatCoordinate(mapClickInfo.longitude)}°
          </Text>
          <LocalCopyButton
            text={`${formatCoordinate(mapClickInfo.latitude)}, ${formatCoordinate(mapClickInfo.longitude)}`}
            customButton={
              <Button btnType="invisible" size="xsmall" my={0} mr={0}>
                <CopyIcon
                  width={16}
                  height={16}
                  viewboxHeight={160}
                  viewboxWidth={160}
                />
              </Button>
            }
          />
        </Box>
      </HeaderAttributeRow>
      <HeaderAttributeRow mb="4px">
        <Text size="small" color="secondaryText">
          <W3wIcon
            width={16}
            height={16}
            viewboxHeight={16}
            viewboxWidth={16}
          />
        </Text>
        <Box horizontal align="center">
          <Text size="small" color="primaryText" textAlign="center">
            {what3Words}
          </Text>
          <LocalCopyButton
            text={what3Words || ''}
            customButton={
              <Button btnType="invisible" size="xsmall" my={0} mr={0}>
                <CopyIcon
                  width={16}
                  height={16}
                  viewboxHeight={160}
                  viewboxWidth={160}
                />
              </Button>
            }
          />
        </Box>
      </HeaderAttributeRow>
    </Box>
  );

  return (
    <PopoverContainer
      ref={popoverRef as React.RefObject<HTMLDivElement>}
      x={position.x}
      y={position.y}
      placement={placement}
      data-name="click-to-identify-popover"
      hasAttributes={attributes.length > 0}
    >
      <div ref={popoverRef}>
        <HeaderSection>
          {featureData?.layerName && (
            <>
              <Box marginBottom="8px">
                <TitleTruncate
                  data-name="popover-title"
                  fontWeight="bold"
                  size="normal"
                  color="primaryText"
                >
                  {featureData.layerName}
                </TitleTruncate>
              </Box>
            </>
          )}
        </HeaderSection>

        {featureData?.isLoading ? (
          <ContentSection>
            <Box justify="center" align="center" py={4}>
              <Loader />
            </Box>
          </ContentSection>
        ) : (
          <ContentSection>
            <ContentSectionItem
              minHeight={`${MIN_CONTENT_HEADER_SECTION_HEIGHT}px`}
            >
              {coordinatesContent}
            </ContentSectionItem>

            {(attributes && attributes.length > 0) || featureData?.isLoading ? (
              <ContentSectionItem mt={'12px'}>
                <Box
                  horizontal
                  justify={'space-between'}
                  align="center"
                  px={3}
                  py={2}
                >
                  <Box
                    horizontal
                    align="center"
                    justify={attributes.length > 1 ? 'space-between' : 'center'}
                    width="100%"
                  >
                    {attributes.length > 1 && (
                      <Button
                        btnType="tertiary"
                        aria-label="Previous"
                        onClick={handlePrevAttribute}
                        size="small"
                      >
                        <ChevronLeftIcon
                          height={16}
                          width={16}
                          viewboxHeight={16}
                          viewboxWidth={16}
                        />
                      </Button>
                    )}

                    <TitleTruncate
                      size="normal"
                      fontWeight="bold"
                      color="primaryText"
                    >
                      {attributes && attributes[attributeIdx]?.label}
                    </TitleTruncate>
                    {attributes.length > 1 && (
                      <Button
                        btnType="tertiary"
                        aria-label="Next"
                        onClick={handleNextAttribute}
                        size="small"
                      >
                        <ChevronRightIcon
                          height={16}
                          width={16}
                          viewboxHeight={16}
                          viewboxWidth={16}
                        />
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box
                  maxHeight={`${MAX_CONTENT_SECTION_ITEM_HEIGHT}px`}
                  minHeight={`${MIN_CONTENT_SECTION_ITEM_HEIGHT}px`}
                  px={3}
                  pb={2}
                >
                  {attributes && attributes[attributeIdx]?.node}
                </Box>
              </ContentSectionItem>
            ) : null}
          </ContentSection>
        )}

        <CloseButton
          data-name="click-to-identify-close-button"
          onClose={handleClose}
          absolute
          top="8px"
          right="8px"
          zIndex="3"
        />
      </div>
    </PopoverContainer>
  );
};

export default ClickToIdentifyPopover;
